import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { BrandingContext } from '../../../../BrandingProvider';
import { Box, IconButton, Tooltip, CircularProgress } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { buildPoPdfPackage, downloadPoPdf } from '../../utils/poPdf';
import { useHistory, useRouteMatch } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import InvDataGrid from '../../components/InvDataGrid';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import LetterheadMenu, { useLetterheadMenu } from '../../components/LetterheadMenu';
import ConfirmDialog from '../../components/ConfirmDialog';
import FilterToolbar from '../../components/FilterToolbar';
import { getPurchaseOrders, deletePurchaseOrder, canDeletePo } from '../../api/procurement';
import { toast } from 'react-toastify';
import { formatDate, formatCurrency, exportToCsv } from '../../utils';
import { IPD_COLORS } from '../../theme';
import PoDetailDrawer from './PoDetailDrawer';
import useVendorLookup from '../../hooks/useVendorLookup';
import { getPoAllowedActions, getPoDisplayStatus } from '../../utils/poLifecycle';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS, filterPoActionsByRole } from '../../utils/inventoryRoles';

const PO_STATUS_FILTERS = [
  'DRAFT',
  'PENDING_APPROVAL',
  'SUBMITTED',
  'APPROVED',
  'SENT_TO_VENDOR',
  'PARTIALLY_RECEIVED',
  'COMPLETED',
  'CLOSED',
  'INVOICE_GENERATED',
  'PAYMENT_COMPLETED',
  'CANCELLED',
];

export default function PoList() {
  const { url } = useRouteMatch();
  const history = useHistory();
  const { can } = useInvRole();
  const { resolveVendorName } = useVendorLookup();
  const brandingData = useContext(BrandingContext);
  const facilityLogo = (() => {
    const img = brandingData?.find(b => b.name === 'collateral_image')?.content;
    return img ? `data:image/png;base64,${img}` : null;
  })();
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedPoId, setSelectedPoId] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const letterheadMenu = useLetterheadMenu();

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const p = {};
      if (search) p.po_number = search;
      if (statusFilter) p.status = statusFilter;
      // API: GET /inventory/purchase-orders/
      const data = await getPurchaseOrders(p);
      setPos(Array.isArray(data) ? data : (data?.items || []));
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  async function handleRowViewPdf(row, letterhead) {
    const poId = row.purchase_order_id || row.id;
    setPdfLoadingId(poId);
    try {
      const pkg = await buildPoPdfPackage(poId, { tenant_id: row.tenant_id, hospital_id: row.hospital_id }, { facilityLogo, letterhead });
      window.open(`${pkg.url}#zoom=page-fit`, '_blank');
      setTimeout(() => URL.revokeObjectURL(pkg.url), 60_000);
    } catch (e) { console.error('PO PDF preview failed', e); }
    finally { setPdfLoadingId(null); }
  }

  async function confirmDelete() {
    if (!can(INV_ACTIONS.PO_DELETE)) return;
    if (!deleteTarget) return;
    const poId = deleteTarget.purchase_order_id || deleteTarget.id;
    setDeleting(true);
    try {
      await deletePurchaseOrder(poId, {
        tenant_id: deleteTarget.tenant_id,
        hospital_id: deleteTarget.hospital_id,
      });
      setDeleteTarget(null);
      if (selectedPoId === poId) setSelectedPoId(null);
      toast.success('Purchase order deleted');
      load();
    } catch (e) {
      toast.error(e?.message || 'Failed to delete purchase order');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }

  async function handleRowDownloadPdf(row, letterhead) {
    const poId = row.purchase_order_id || row.id;
    setPdfLoadingId(poId);
    try {
      await downloadPoPdf(poId, { tenant_id: row.tenant_id, hospital_id: row.hospital_id }, { facilityLogo, letterhead });
    } catch (e) { console.error('PO PDF download failed', e); }
    finally { setPdfLoadingId(null); }
  }

  // Fixed chip width from longest known/data label so PARTIALLY RECEIVED and COMPLETED align.
  const statusChipMinWidth = useMemo(() => {
    const fromData = pos
      .map(r => {
        const s = getPoDisplayStatus(r);
        return s ? String(s).replace(/_/g, ' ') : '';
      })
      .filter(Boolean);
    const labels = [...new Set([
      ...PO_STATUS_FILTERS.map(s => s.replace(/_/g, ' ')),
      ...fromData,
    ])];
    return Math.ceil(Math.max(...labels.map(l => l.length)) * 8 + 24);
  }, [pos]);

  const approvalChipMinWidth = useMemo(() => {
    const fromData = pos
      .map(r => (r.approval_status ? String(r.approval_status).replace(/_/g, ' ') : ''))
      .filter(Boolean);
    const labels = fromData.length
      ? [...new Set(fromData)]
      : ['APPROVED', 'PENDING APPROVAL', 'REJECTED', 'NOT REQUIRED'];
    return Math.ceil(Math.max(...labels.map(l => l.length)) * 8 + 24);
  }, [pos]);

  const statusColumnWidth = Math.max(180, statusChipMinWidth + 28);
  const approvalColumnWidth = Math.max(130, approvalChipMinWidth + 28);

  const columns = useMemo(() => [
    { field: 'po_number', headerName: 'PO #', width: 150 },
    { field: 'vendor_id', headerName: 'Vendor', flex: 1, minWidth: 160,
      valueGetter: p => resolveVendorName(p.row.vendor_id) },
    { field: 'expected_delivery_date', headerName: 'Expected', width: 120, valueFormatter: p => formatDate(p.value) },
    {
      field: 'status',
      headerName: 'Status',
      width: statusColumnWidth,
      renderCell: (p) => (
        <StatusChip status={getPoDisplayStatus(p.row)} minWidth={statusChipMinWidth} />
      ),
    },
    {
      field: 'approval_status',
      headerName: 'Approval',
      width: approvalColumnWidth,
      renderCell: p => (p.value
        ? <StatusChip status={p.value} minWidth={approvalChipMinWidth} />
        : '—'),
    },
    { field: 'created_at', headerName: 'Created', width: 120, valueFormatter: p => formatDate(p.value) },
    { field: 'actions', headerName: '', width: 180, sortable: false,
      renderCell: (p) => {
        const rowId = p.row.purchase_order_id || p.row.id;
        const isLoadingPdf = pdfLoadingId === rowId;
        const actions = filterPoActionsByRole(getPoAllowedActions(p.row), can);
        return (
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Tooltip title="View details"><IconButton size="small"
              onClick={() => setSelectedPoId(rowId)}
              sx={{ color: IPD_COLORS.primary }}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
            {actions.has('delete') && canDeletePo(p.row) && (
              <Tooltip title="Delete"><IconButton size="small"
                onClick={() => setDeleteTarget(p.row)}
                sx={{ color: IPD_COLORS.error.text }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
            )}
            {actions.has('create_grn') && (
              <Tooltip title="Create GRN"><IconButton size="small"
                onClick={() => history.push(`${url.replace('/pos', '')}/grn/new?po=${rowId}`)}
                sx={{ color: IPD_COLORS.success.text }}><ReceiptIcon fontSize="small" /></IconButton></Tooltip>
            )}
            {isLoadingPdf
              ? <CircularProgress size={16} sx={{ color: IPD_COLORS.primary, mx: 0.5 }} />
              : <>
                  <Tooltip title="View PDF"><IconButton size="small"
                    onClick={(e) => letterheadMenu.open(e, (lh) => handleRowViewPdf(p.row, lh))}
                    sx={{ color: IPD_COLORS.primary }}><PictureAsPdfIcon fontSize="small" /></IconButton></Tooltip>
                  <Tooltip title="Download PDF"><IconButton size="small"
                    onClick={(e) => letterheadMenu.open(e, (lh) => handleRowDownloadPdf(p.row, lh))}
                    sx={{ color: IPD_COLORS.primary }}><DownloadIcon fontSize="small" /></IconButton></Tooltip>
                </>
            }
          </Box>
        );
      } },
  ], [
    url, history, can, resolveVendorName, pdfLoadingId, letterheadMenu,
    statusChipMinWidth, statusColumnWidth, approvalChipMinWidth, approvalColumnWidth,
  ]);

  return (
    <Box>
      <LetterheadMenu menu={letterheadMenu} label="Purchase Order" />
      <PageHeader eyebrow="Material Management" title="Purchase Orders"
        description={`${pos.length} orders`}
        actions={
          <>
            <InvButton startIcon={<DownloadIcon />} variant="outlined" tone="teal" size="small"
              onClick={() => exportToCsv('purchase-orders.csv', pos)}>Export</InvButton>
            {can(INV_ACTIONS.PO_CREATE) && (
              <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${url.replace('/pos', '')}/po/new`)}>New PO</InvButton>
            )}
          </>
        }
      />


      <FilterToolbar>
        <FilterToolbar.Search
          placeholder="Search by PO#..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <FilterToolbar.Filter
          label="Status"
          width="wide"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: 'All' },
            ...PO_STATUS_FILTERS.map(s => ({ value: s, label: s })),
          ]}
        />
      </FilterToolbar>
      <InvDataGrid fillHeight rows={pos} columns={columns} loading={loading} error={error} onRetry={load}
        getRowId={r => r.purchase_order_id || r.id} emptyMessage="No purchase orders found."
        emptyAction={can(INV_ACTIONS.PO_CREATE)
          ? <InvButton startIcon={<AddIcon />} onClick={() => history.push(`${url.replace('/pos', '')}/po/new`)}>Create PO</InvButton>
          : null} />

      <PoDetailDrawer
        poId={selectedPoId}
        open={Boolean(selectedPoId)}
        onClose={() => setSelectedPoId(null)}
        onRefreshed={load}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="Delete Purchase Order"
        description={`Delete ${deleteTarget?.po_number || 'this purchase order'}? It will be deactivated and removed from the list.`}
        destructive
        loading={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </Box>
  );
}
